import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { asPlaceholder } from "../../i18n/filterLabels";
import GetData from "../../services/getData";
import { useDebouncedSearch } from "../../hooks/useDebouncedSearch";
import TitleWithIcon from "../../components/layout/pageTitle/PageTitle";
import {
  Col,
  Container,
  Row,
  Table,
} from "react-bootstrap";
import employeesIcon from "../../media/icons8-hierarchy.png";
import BlockTitle from "../../components/layout/blockTitle/BlockTitle";
import { Button } from "../../components/ui";
import filterIcon from "../../media/filter_icon.png";
import EditableModal from "../../components/layout/editableModal/EditableModal";

const TYPE_OPTIONS = (t) => [
  { value: "Врач", label: t("types.doctor") },
  { value: "Медсестра/Медбрат", label: t("types.nurse") },
  { value: "Администратор", label: t("types.admin") },
];

const EMPTY_EMPLOYEE_FORM = {
  name: "",
  type: "Врач",
  post: "",
  phone: "",
};

const EMPLOYEE_FIELDS = (t, typeOptions) => [
  { name: "name", label: t("fields.name"), required: true, bold: true },
  {
    name: "type",
    label: t("fields.category"),
    type: "select",
    options: typeOptions,
    required: true,
  },
  { name: "post", label: t("fields.post"), type: "textarea", rows: 3 },
  { name: "phone", label: t("fields.phone") },
];

const unwrapField = (field, fallback = "") => {
  if (field && typeof field === "object" && "value" in field) {
    return field.value ?? fallback;
  }
  return field ?? fallback;
};

function Employees() {
  const { t } = useTranslation("staff");
  const typeOptions = useMemo(() => TYPE_OPTIONS(t), [t]);
  const employeeFields = useMemo(
    () => EMPLOYEE_FIELDS(t, typeOptions),
    [t, typeOptions]
  );
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ type: "all" });
  const {
    searchTerm,
    setSearchTerm,
    debouncedSearchTerm,
    searchInputRef,
    captureSearchFocus,
    restoreSearchFocus,
  } = useDebouncedSearch();
  const [isFiltersActive, setIsFiltersActive] = useState(false);
  // === Модальное окно ===
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formInitialData, setFormInitialData] = useState(EMPTY_EMPLOYEE_FORM);

  // Загрузка списка сотрудников
  const loadEmployees = async () => {
    const preserveSearchFocus = captureSearchFocus();
    const timer = setTimeout(() => setLoading(true), 300);
    setError(null);
    try {
      const data = await GetData({
        module: "staff",
        block: "list",
        type: filters.type === "all" ? "" : filters.type,
        search: debouncedSearchTerm.trim(),
      });
      console.log(data);
      setEmployees(data.result || []);
    } catch (err) {
      console.error("Ошибка загрузки сотрудников:", err);
      setError(err.message || t("list.errors.loadFailed"));
    } finally {
      clearTimeout(timer);
      setLoading(false);
      restoreSearchFocus(preserveSearchFocus);
    }
  };

  useEffect(() => {
    document.title = t("list.title");
  }, [t]);

  useEffect(() => {
    loadEmployees();
  }, [filters.type, debouncedSearchTerm]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
  };

  // Есть ли выбранный фильтр: подсвечиваем такие поля, кнопки «Сбросить» больше нет.
  const hasActiveFilters =
    searchTerm.trim() !== "" || filters.type !== "all";

  // === Работа с модальным окном ===
  const handleAddClick = () => {
    setEditingId(null);
    setFormInitialData({ ...EMPTY_EMPLOYEE_FORM });
    setShowModal(true);
  };

  const handleEditClick = async (employee) => {
    const id = unwrapField(employee?.id);
    if (!id) return;

    const openWithData = (employeeData) => {
      setEditingId(Number(id));
      setFormInitialData({
        name: unwrapField(employeeData.name),
        type: unwrapField(employeeData.type) || "Врач",
        post: unwrapField(employeeData.post),
        phone: unwrapField(employeeData.phone),
      });
      setShowModal(true);
    };

    try {
      const data = await GetData({
        module: "staff",
        block: "info",
        id_staff: id,
      });
      let employeeData = data.result || employee;
      if (Array.isArray(employeeData) && employeeData.length > 0) {
        employeeData = employeeData[0];
      }
      openWithData(employeeData);
    } catch (err) {
      console.error("Ошибка загрузки сотрудника:", err);
      openWithData(employee);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingId(null);
  };

  const handleSaveEmployee = async (formData) => {
    try {
      if (editingId) {
        await GetData({
          module: "staff",
          block: "info",
          id_staff: editingId,
          action: "change",
          data: {
            name: { value: formData.name },
            type: { value: formData.type },
            post: { value: formData.post },
            phone: { value: formData.phone },
          },
        });
      } else {
        await GetData({
          module: "staff",
          block: "list",
          action: "add",
          name: formData.name,
          type: formData.type,
          post: formData.post,
          phone: formData.phone,
        });
      }
      await loadEmployees();
    } catch (err) {
      console.error("Ошибка сохранения:", err);
      alert(t("list.errors.saveFailed"));
      return false;
    }
  };

  console.log("Staff: ", employees);

  return (
    <Container fluid="md">
      <Row>
        <Col>
          <BlockTitle
            title={
              <TitleWithIcon
                icon={employeesIcon}
                title={t("list.title")}
                showStatus={false}
                status={""}
              />
            }
            buttons={[
              {
                label: t("list.add"),
                onClick: handleAddClick,
                type: "normal",
              },
            ]}
          />
        </Col>
      </Row>

      {/* Основной контент */}
      <Row>
        <Col>
          <div className="page">
            <div className="page-content">
              {/* Панель поиска */}
              <div className="page-block">
                <div
                  className={`filters-panel ${isFiltersActive ? "active" : ""} ${
                    hasActiveFilters ? "has-active-filters" : ""
                  }`}
                >
                  <div className="filters-panel-title">
                    <div className="filters-panel-title-icon">
                      <img
                        className="title-with-icon__image"
                        src={filterIcon}
                        alt={t("filters.alt", { ns: "common" })}
                        title={t("filters.alt", { ns: "common" })}
                      />
                      <div className="filters-panel-name">
                        {t("filters.title", { ns: "common" })}
                      </div>
                    </div>
                    <Button
                      onClick={() => setIsFiltersActive((prev) => !prev)}
                      size="sm"
                      variant="secondary">
                      {isFiltersActive
                        ? t("filters.hide", { ns: "common" })
                        : t("filters.show", { ns: "common" })}
                    </Button>
                  </div>
                  <div className="search-section">
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder={t("list.searchPlaceholder")}
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className={`search-input ${searchTerm.trim() ? "is-filter-active" : ""}`}
                    />
                  </div>
                  <div className="filters-section">
                    <div
                      className={`filter-group ${filters.type !== "all" ? "is-filter-active" : ""}`}
                    >
                      <select
                        className={
                          filters.type === "all" ? "is-placeholder" : ""
                        }
                        value={filters.type}
                        onChange={(e) =>
                          handleFilterChange("type", e.target.value)
                        }
                      >
                        <option value="all">
                          {asPlaceholder(t("list.categoryLabel"))}
                        </option>
                        <option value="1">{t("types.doctor")}</option>
                        <option value="2">{t("types.nurse")}</option>
                        <option value="3">{t("types.admin")}</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Список услуг */}
              <div className="page-block table-list">
                {error ? (
                  <div className="text-danger">{t("error", { ns: "common", message: error })}</div>
                ) : loading ? (
                  <div>{t("list.loading")}</div>
                ) : employees.length === 0 ? (
                  <div className="no-patients">{t("list.notFound")}</div>
                ) : (
                  <Table className="main-table-list rounded-table">
                    <thead>
                      <tr>
                        <th>{t("list.colName")}</th>
                        <th>{t("list.colPost")}</th>
                        <th>{t("list.colPhone")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employees.map((employee) => (
                        <tr
                          key={unwrapField(employee?.id) || employee?.id}
                          className="table-item-link"
                          onClick={() => handleEditClick(employee)}
                          style={{ cursor: "pointer" }}
                        >
                          <td>
                            <div className="table-item-name">
                              {unwrapField(employee?.name)}
                            </div>
                          </td>
                          <td>{unwrapField(employee?.post)}</td>
                          <td>
                            {unwrapField(employee?.phone) === ""
                              ? t("list.notSpecified")
                              : unwrapField(employee?.phone)}
                          </td>
                        </tr>
                      ))}
                      <tr className="table-summary">
                        <td colSpan={4} className="summary-cell">
                          {t("list.found", { count: employees.length })}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                )}
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* === МОДАЛЬНОЕ ОКНО === */}
      <EditableModal
        show={showModal}
        onHide={handleModalClose}
        title={editingId ? t("list.editTitle") : t("list.addTitle")}
        initialData={formInitialData}
        fields={employeeFields}
        onSave={handleSaveEmployee}
      />
    </Container>
  );
}

export default Employees;
